import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Users, Send, Sparkles, MessageCircle, History, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

// Reuse chart renderer from chat sidebar
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };

function parseCharts(content: string) {
  const parts: Array<{ type: 'text' | 'chart'; content: string; chartData?: any }> = [];
  const regex = /(?:```(?:json)?\s*)?\[CHART\]\s*([\s\S]*?)\s*\[\/CHART\](?:\s*```)?/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    try { parts.push({ type: 'chart', content: '', chartData: JSON.parse(match[1].trim()) }); }
    catch { parts.push({ type: 'text', content: match[0] }); }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) parts.push({ type: 'text', content: content.slice(lastIndex) });
  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

function ChartBlock({ chartData }: { chartData: any }) {
  const { type, title, data, keys } = chartData;
  if (!data || data.length === 0) return null;
  const normalizedType = (type || '').toLowerCase().replace(/[_\s-]/g, '');

  return (
    <div className="my-3 p-3 bg-muted/30 rounded-lg">
      {title && <p className="text-xs font-semibold mb-2 text-center">{title}</p>}
      <ResponsiveContainer width="100%" height={280}>
        {normalizedType === 'pie' || normalizedType === 'donut' ? (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={normalizedType === 'donut' ? 90 : 100} innerRadius={normalizedType === 'donut' ? 50 : 0} activeShape={null as any}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        ) : normalizedType === 'horizontalbar' ? (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={120} />
            <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false} />
          </BarChart>
        ) : normalizedType === 'line' ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} />
            <YAxis tick={{ fontSize: 10, fill: '#888' }} />
            <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
            <Legend />
            {keys ? keys.map((key: string, i: number) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />) :
              <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />}
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#888' }} />
            <YAxis tick={{ fontSize: 10, fill: '#888' }} />
            <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
            <Legend />
            {keys ? keys.map((key: string, i: number) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} activeBar={false} />) :
              <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
                {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const SUGGESTED_QUERIES = [
  'Which clients have the highest revenue?',
  'Show me all clients with expenses above 80% of turnover',
  'Compare profit margins across all clients',
  'Which clients have declining profitability?',
  'Show a bar chart of revenue by client',
  'Which clients are most at risk of ATO audit?',
  'Rank clients by net profit with a chart',
  'Which clients should we focus on for year-end tax planning?',
];

export default function MultiClientQueryPage() {
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState<Message[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load conversations
  useEffect(() => {
    apiClient.get('/multi-client/conversations').then(r => setConversations(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [history]);

  // Auto-save conversation
  const saveConversation = async (msgs: Message[]) => {
    if (msgs.length < 2) return;
    try {
      const words = (msgs.find(m => m.role === 'user')?.content || '').split(/\s+/).slice(0, 4).join(' ');
      const title = words ? words + '...' : 'Query';
      const r = await apiClient.post('/multi-client/conversations', { id: conversationId, title, messages: msgs });
      if (!conversationId && r.data.data?.id) setConversationId(r.data.data.id);
      apiClient.get('/multi-client/conversations').then(r => setConversations(r.data.data || [])).catch(() => {});
    } catch {}
  };

  const loadConversation = async (id: string) => {
    try {
      const r = await apiClient.get(`/multi-client/conversations/${id}`);
      setHistory(r.data.data.messages || []);
      setConversationId(id);
      setShowHistory(false);
    } catch { toast.error('Failed to load'); }
  };

  const startNew = () => { setHistory([]); setConversationId(null); setShowHistory(false); };

  const queryMutation = useMutation({
    mutationFn: async (msg: string) => {
      const r = await apiClient.post('/multi-client/query', { message: msg });
      return r.data.data;
    },
    onSuccess: (data) => {
      const updated = [...history, data.message];
      setHistory(prev => [...prev, data.message]);
      setMessage('');
      saveConversation(updated);
    },
    onError: () => toast.error('Failed to process query'),
  });

  const handleSend = () => {
    if (!message.trim() || queryMutation.isPending) return;
    const userMsg: Message = { id: `user-${Date.now()}`, role: 'user', content: message, timestamp: new Date().toISOString() };
    setHistory(prev => [...prev, userMsg]);
    queryMutation.mutate(message);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] relative overflow-hidden">
      <PageHeader
        icon={Users}
        title="Multi-Client Intelligence"
        description="Ask questions across all your Xero-connected clients at once"
      />


      {/* Chat Area */}
      <Card className="flex-1 overflow-hidden mb-4">
        <CardContent className="h-full p-0">
          <ScrollArea className="h-full px-4" ref={scrollRef}>
            {history.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Ask about all your clients</h3>
                <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
                  Query financial data across all Xero-connected clients simultaneously. Compare performance, identify trends, and spot opportunities.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl">
                  {SUGGESTED_QUERIES.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setMessage(q); setTimeout(() => inputRef.current?.focus(), 100); }}
                      className="text-left px-3 py-2 text-xs bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-4 space-y-4">
                {history.map((msg) => (
                  <div key={msg.id} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role === 'assistant' && (
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <Sparkles className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div className={cn('max-w-[85%] rounded-lg p-3 text-sm', msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
                      {msg.role === 'assistant' ? (
                        <div>
                          {parseCharts(msg.content).map((part, i) =>
                            part.type === 'chart' && part.chartData ? (
                              <ChartBlock key={i} chartData={part.chartData} />
                            ) : (
                              <div key={i} className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-headings:my-1 overflow-x-auto">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                  table: ({ children, ...props }) => <div className="my-2 overflow-x-auto"><table {...props} className="text-xs w-full border-collapse" style={{ border: '1px solid #d1d5db' }}>{children}</table></div>,
                                  th: ({ children, ...props }) => <th {...props} className="text-left p-2 font-semibold bg-muted/60 text-xs" style={{ border: '1px solid #d1d5db' }}>{children}</th>,
                                  td: ({ children, ...props }) => <td {...props} className="p-2 text-xs" style={{ border: '1px solid #d1d5db' }}>{children}</td>,
                                }}>{part.content}</ReactMarkdown>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {queryMutation.isPending && (
                  <div className="flex gap-2">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                    </div>
                    <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground">
                      Analyzing data across all clients...
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Input */}
      <Card>
        <CardContent className="pt-3 pb-3">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about all clients... e.g., 'Which clients have the highest profit margin?'"
              className="flex-1 resize-none bg-transparent border-0 outline-none text-sm min-h-[40px] max-h-[100px]"
              rows={1}
            />
            <Button size="sm" onClick={handleSend} disabled={!message.trim() || queryMutation.isPending}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History icon */}
      {!showHistory && (
        <button onClick={() => setShowHistory(true)} className="absolute bottom-[100px] right-4 z-30 text-muted-foreground/50 hover:text-primary transition-colors" title="Chat History">
          <History className="h-4 w-4" />
          {conversations.length > 0 && <span className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-destructive text-[7px] text-white flex items-center justify-center font-bold">{conversations.length > 9 ? '9+' : conversations.length}</span>}
        </button>
      )}

      {/* Sliding History Panel */}
      <div className={cn('absolute top-0 right-0 h-full w-72 bg-background border-l shadow-xl z-40 flex flex-col transition-transform duration-300', showHistory ? 'translate-x-0' : 'translate-x-full')}>
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2"><History className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">Query History</h3></div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={startNew}><Plus className="h-3 w-3 mr-1" />New</Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowHistory(false)}><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <ScrollArea className="flex-1 p-2">
          {conversations.length === 0 ? (
            <div className="text-center py-12"><MessageCircle className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" /><p className="text-xs text-muted-foreground">No past queries</p></div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv: any) => (
                <div key={conv.id} className={cn('flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/80 text-xs group', conversationId === conv.id && 'bg-primary/10 border border-primary/20')} onClick={() => loadConversation(conv.id)}>
                  <MessageCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium max-w-[160px]">{conv.title}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(conv.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} · {conv.message_count || 0} msgs</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-30 hover:opacity-100 flex-shrink-0" onClick={async (e) => {
                    e.stopPropagation();
                    await apiClient.delete(`/multi-client/conversations/${conv.id}`);
                    setConversations(prev => prev.filter(c => c.id !== conv.id));
                    if (conversationId === conv.id) startNew();
                  }}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
